import { TypingEngine } from './engine';
import { TypingArea } from './ui/typing-area';
import { SmoothCaret } from './ui/caret';
import { StatsDisplay } from './ui/stats';
import { ModeSelector } from './ui/mode-selector';
import { SoundManager } from './sound';
import { SHORTCUTS } from './constants';
export class TyperacerApp {
    constructor() {
        this.isTestActive = false;
        this.statsUpdateInterval = null;
        this.mobileInput = null;
        this.lastInputValue = '';
        this.isComposing = false;
        this.autoPausedByVisibility = false;
        this.engine = new TypingEngine();
        this.config = this.loadConfig();
        this.isMobile =
            'ontouchstart' in window ||
                navigator.maxTouchPoints > 0 ||
                /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        this.typingArea = new TypingArea('typing-area-container');
        this.caret = new SmoothCaret('typing-area-container');
        this.statsDisplay = new StatsDisplay('stats-display-container');
        this.modeSelector = new ModeSelector('mode-selector-container', (mode) => this.handleModeChange(mode), (config) => this.handleConfigChange(config));
        this.soundManager = new SoundManager();
        this.soundManager.setEnabled(this.config.soundEnabled);
        this.modeSelector.setConfig(this.config);
        this.setupEventListeners();
        this.initializeSession().catch((err) => {
            console.error('Initial session failed:', err);
        });
    }
    async initializeSession() {
        this.hideSoftFail();
        try {
            const isFirstLoad = !sessionStorage.getItem('typeracer-dictionary-loaded');
            const session = await this.engine.initializeSession(this.config, isFirstLoad);
            if (isFirstLoad) {
                sessionStorage.setItem('typeracer-dictionary-loaded', 'true');
            }
            if (!session) {
                // soft_fail already emitted by engine
                return;
            }
            this.typingArea.renderWords(session.words);
            this.typingArea.setMode(this.config.mode);
            this.typingArea.setFontSize(this.config.fontSize);
            this.typingArea.setBlindMode(this.config.blindMode);
            this.typingArea.setActiveWord(0, 0);
            this.caret.setVisible(false);
            const notice = this.engine.getSoftNotice();
            if (notice) {
                this.showSoftNotice(notice);
            }
        }
        catch (error) {
            console.error('Failed to initialize session:', error);
            this.showSoftFail({
                code: 'init_failed',
                message: 'Seansi käivitamine ebaõnnestus. Proovi uuesti.',
                recoverable: true,
            });
        }
    }
    normalizeMobileCharacter(char) {
        if (!char || char.length === 0)
            return char;
        let normalized = char.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        normalized = normalized.replace(/['']/g, "'");
        normalized = normalized.replace(/[""]/g, '"');
        normalized = normalized.replace(/[—–]/g, '-');
        normalized = normalized.replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ');
        normalized = normalized.replace(/[\u200B-\u200D\uFEFF]/g, '');
        normalized = normalized.replace(/[ﬁ]/g, 'fi');
        normalized = normalized.replace(/[ﬂ]/g, 'fl');
        normalized = normalized.replace(/[ﬀ]/g, 'ff');
        normalized = normalized.replace(/[ﬃ]/g, 'ffi');
        normalized = normalized.replace(/[ﬄ]/g, 'ffl');
        return normalized;
    }
    isFocusInForeignInput(target) {
        if (!(target instanceof HTMLElement))
            return false;
        if (target === this.mobileInput)
            return false;
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT')
            return true;
        if (target.isContentEditable)
            return true;
        // Leaderboard / modal UI
        if (target.closest('.leaderboard-modal, .modal, [role="dialog"], #leaderboard-form, .leaderboard-panel')) {
            return true;
        }
        return false;
    }
    showSoftFail(payload) {
        const container = document.getElementById('typing-area-container');
        if (!container)
            return;
        this.isTestActive = false;
        this.stopStatsUpdates();
        this.caret.setVisible(false);
        document.body.classList.remove('typeracer-test-active');
        container.innerHTML = `
      <div class="typeracer-soft-error flex flex-col items-center justify-center gap-4 p-8 text-center" role="alert">
        <p class="text-gray-200 text-lg">${payload.message}</p>
        ${payload.recoverable
            ? `<button type="button" id="typeracer-soft-retry" class="px-4 py-2 rounded-lg bg-yellow-400/20 text-yellow-400 border border-yellow-400/40 hover:bg-yellow-400/30 transition-colors min-h-[44px]">
                Proovi uuesti
              </button>`
            : ''}
        <button type="button" id="typeracer-soft-reset" class="px-4 py-2 rounded-lg text-gray-400 border border-gray-600 hover:border-gray-400 transition-colors min-h-[44px]">
          Lähtesta
        </button>
      </div>
    `;
        const retryBtn = document.getElementById('typeracer-soft-retry');
        if (retryBtn) {
            retryBtn.addEventListener('click', () => {
                this.resetTest();
            });
        }
        const resetBtn = document.getElementById('typeracer-soft-reset');
        if (resetBtn) {
            resetBtn.addEventListener('click', () => {
                this.resetTest();
            });
        }
    }
    hideSoftFail() {
        // Rendering words replaces soft-error UI
    }
    showSoftNotice(message) {
        let banner = document.getElementById('typeracer-soft-notice');
        if (!banner) {
            banner = document.createElement('div');
            banner.id = 'typeracer-soft-notice';
            banner.className =
                'text-sm text-yellow-400/90 text-center py-2 px-3 mb-2 rounded border border-yellow-400/20 bg-yellow-400/5';
            const container = document.getElementById('typing-area-container');
            container?.parentElement?.insertBefore(banner, container);
        }
        banner.textContent = message;
        banner.style.display = 'block';
        window.setTimeout(() => {
            if (banner)
                banner.style.display = 'none';
        }, 5000);
    }
    setupEventListeners() {
        this.engine.on('keystroke', () => {
            const session = this.engine.getSession();
            if (session) {
                this.typingArea.setActiveWord(session.activeWordIndex, session.activeLetterIndex);
                this.updateStats();
                if (this.isMobile && this.isTestActive) {
                    requestAnimationFrame(() => {
                        const activeWord = document.querySelector('.word.active');
                        if (activeWord) {
                            activeWord.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                                inline: 'nearest',
                            });
                        }
                    });
                }
            }
            this.soundManager.playClick('default');
        });
        this.engine.on('test_start', () => {
            this.isTestActive = true;
            this.startStatsUpdates();
        });
        this.engine.on('soft_fail', (payload) => {
            this.showSoftFail(payload);
        });
        this.engine.on('error', (payload) => {
            // Non-fatal refill / keystroke errors — quiet notice, stay in race
            if (payload?.code === 'refill_failed' ||
                payload?.code === 'refill_empty') {
                this.showSoftNotice(payload.message || 'Sõnade juurde laadimine ebaõnnestus');
            }
        });
        this.engine.on('words_exhausted', () => {
            if (this.config.mode !== 'time' || !this.isTestActive)
                return;
            this.engine
                .generateMoreWords(false)
                .then((ok) => {
                if (!ok)
                    return;
                const session = this.engine.getSession();
                if (session) {
                    this.typingArea.setMode(this.config.mode);
                    this.typingArea.renderWords(session.words);
                    this.typingArea.setActiveWord(session.activeWordIndex, session.activeLetterIndex);
                }
            })
                .catch((err) => {
                console.error('words_exhausted handler failed:', err);
                this.showSoftNotice('Sõnade juurde laadimine ebaõnnestus');
            });
        });
        this.engine.on('words_appended', () => {
            const session = this.engine.getSession();
            if (session) {
                this.typingArea.setMode(this.config.mode);
                this.typingArea.renderWords(session.words);
                this.typingArea.setActiveWord(session.activeWordIndex, session.activeLetterIndex);
            }
        });
        this.engine.on('test_complete', () => {
            this.soundManager.playClick('success');
            this.isTestActive = false;
            this.autoPausedByVisibility = false;
            this.stopStatsUpdates();
            document.body.classList.remove('typeracer-test-active');
            const session = this.engine.getSession();
            if (!session || !session.endTime) {
                this.showSoftFail({
                    code: 'missing_session',
                    message: 'Seanss katkes ootamatult. Proovi uuesti.',
                    recoverable: true,
                });
                this.refreshAndRestart().catch((err) => console.error('refresh after incomplete session failed:', err));
                return;
            }
            // Pause-aware elapsed for early-finish vs time-up decision
            const elapsedTime = this.engine.getActiveElapsedSeconds(session.endTime);
            let shouldShowResults = false;
            if (this.config.mode === 'time' && this.config.duration) {
                shouldShowResults = elapsedTime >= this.config.duration - 0.5;
            }
            else if (this.config.mode === 'words' ||
                this.config.mode === 'paragraphs') {
                shouldShowResults = true;
            }
            if (shouldShowResults) {
                this.showResults();
            }
            else {
                this.refreshAndRestart().catch((err) => {
                    console.error('refreshAndRestart failed:', err);
                    this.showSoftFail({
                        code: 'refresh_failed',
                        message: 'Uuendamine ebaõnnestus. Proovi uuesti.',
                        recoverable: true,
                    });
                });
            }
        });
        let tabPressed = false;
        if (this.isMobile) {
            this.mobileInput = document.createElement('input');
            this.mobileInput.type = 'text';
            this.mobileInput.className = 'typeracer-mobile-input';
            this.mobileInput.style.position = 'fixed';
            this.mobileInput.style.opacity = '0';
            this.mobileInput.style.width = '1px';
            this.mobileInput.style.height = '1px';
            this.mobileInput.style.left = '0';
            this.mobileInput.style.top = '0';
            this.mobileInput.style.border = 'none';
            this.mobileInput.style.outline = 'none';
            this.mobileInput.style.background = 'transparent';
            this.mobileInput.style.padding = '0';
            this.mobileInput.style.margin = '0';
            this.mobileInput.style.zIndex = '-1';
            this.mobileInput.setAttribute('autocomplete', 'off');
            this.mobileInput.setAttribute('autocorrect', 'off');
            this.mobileInput.setAttribute('autocapitalize', 'off');
            this.mobileInput.setAttribute('spellcheck', 'false');
            this.mobileInput.setAttribute('inputmode', 'text');
            this.mobileInput.setAttribute('tabindex', '-1');
            document.body.appendChild(this.mobileInput);
            // Block paste into mobile input
            this.mobileInput.addEventListener('paste', (e) => {
                e.preventDefault();
            });
            this.mobileInput.addEventListener('beforeinput', (e) => {
                if (e.inputType === 'insertFromPaste' ||
                    e.inputType === 'insertFromDrop' ||
                    e.inputType === 'insertReplacementText') {
                    e.preventDefault();
                }
            });
            this.mobileInput.addEventListener('compositionstart', () => {
                this.isComposing = true;
            });
            this.mobileInput.addEventListener('compositionend', () => {
                // Let the subsequent `input` event process the composed text once
                this.isComposing = false;
            });
            this.mobileInput.addEventListener('input', (e) => {
                // Skip during composition — compositionend + input would double-fire
                if (this.isComposing)
                    return;
                const target = e.target;
                const currentValue = target.value;
                if (!this.isTestActive && currentValue.length > 0) {
                    this.startTest();
                }
                if (!this.isTestActive) {
                    target.value = '';
                    this.lastInputValue = '';
                    return;
                }
                const session = this.engine.getSession();
                if (session?.isPaused) {
                    target.value = '';
                    this.lastInputValue = '';
                    return;
                }
                if (currentValue.length > this.lastInputValue.length) {
                    const newChars = currentValue.slice(this.lastInputValue.length);
                    // Reject burst inserts (paste/autofill) — only accept one char at a time
                    if (newChars.length > 1) {
                        target.value = '';
                        this.lastInputValue = '';
                        return;
                    }
                    for (let i = 0; i < newChars.length; i++) {
                        const char = newChars[i];
                        if (char === '\n' || char === '\r' || char === '\t')
                            continue;
                        const normalizedChar = this.normalizeMobileCharacter(char);
                        this.engine.processKeystroke(normalizedChar, Date.now());
                    }
                    target.value = '';
                    this.lastInputValue = '';
                }
                else if (currentValue.length < this.lastInputValue.length) {
                    const removedCount = this.lastInputValue.length - currentValue.length;
                    for (let i = 0; i < Math.min(removedCount, 1); i++) {
                        this.engine.processKeystroke('Backspace', Date.now());
                    }
                    target.value = '';
                    this.lastInputValue = '';
                }
            });
            this.mobileInput.addEventListener('keydown', (e) => {
                if (this.isTestActive && e.key === ' ') {
                    e.preventDefault();
                }
                if (e.key === 'Backspace' && this.isTestActive) {
                    e.preventDefault();
                }
                if (e.key === SHORTCUTS.PAUSE && this.isTestActive) {
                    e.preventDefault();
                    this.togglePause();
                }
            });
            this.mobileInput.addEventListener('blur', () => {
                if (this.isTestActive && !this.engine.getSession()?.isPaused) {
                    setTimeout(() => {
                        if (this.isTestActive &&
                            this.mobileInput &&
                            !this.engine.getSession()?.isPaused) {
                            this.mobileInput.focus();
                        }
                    }, 100);
                }
            });
        }
        // Desktop paste/copy/cut guards on the game surface
        document.addEventListener('paste', (e) => {
            if (this.isTestActive) {
                e.preventDefault();
            }
        });
        document.addEventListener('copy', (e) => {
            const typingArea = document.getElementById('typing-area-container');
            if (this.isTestActive &&
                typingArea &&
                (e.target === typingArea || typingArea.contains(e.target))) {
                e.preventDefault();
            }
        });
        document.addEventListener('cut', (e) => {
            if (this.isTestActive) {
                e.preventDefault();
            }
        });
        document.addEventListener('keydown', (e) => {
            if (this.isFocusInForeignInput(e.target)) {
                return;
            }
            if (this.isMobile && this.mobileInput) {
                if (e.key.length === 1 && e.target !== this.mobileInput) {
                    this.mobileInput.focus();
                    return;
                }
            }
            const modifierKeys = ['Shift', 'Control', 'Alt', 'Meta', 'CapsLock'];
            if (modifierKeys.includes(e.key)) {
                return;
            }
            if (e.key === 'Tab') {
                tabPressed = true;
                e.preventDefault();
                return;
            }
            if (tabPressed && e.key === 'Enter') {
                e.preventDefault();
                tabPressed = false;
                this.resetTest();
                return;
            }
            tabPressed = false;
            // Escape always toggles pause while racing or paused
            if (e.key === SHORTCUTS.PAUSE) {
                const phase = this.engine.getPhase();
                if (phase === 'racing' || phase === 'paused' || this.isTestActive) {
                    e.preventDefault();
                    this.togglePause();
                    return;
                }
            }
            if (this.isTestActive && e.key === ' ') {
                e.preventDefault();
            }
            if (!this.isMobile || e.key.length !== 1) {
                const session = this.engine.getSession();
                if (session?.isPaused) {
                    return;
                }
                if (this.isTestActive) {
                    this.engine.processKeystroke(e.key, Date.now());
                }
                else if (e.key !== 'Tab' && e.key !== SHORTCUTS.PAUSE) {
                    const phase = this.engine.getPhase();
                    if (phase === 'ready') {
                        this.startTest();
                        this.engine.processKeystroke(e.key, Date.now());
                    }
                }
            }
        });
        document.addEventListener('keyup', (e) => {
            if (e.key === 'Tab') {
                tabPressed = false;
            }
            if (this.isTestActive || this.engine.getSession()?.isActive) {
                this.engine.processKeyup(e.key, Date.now());
            }
        });
        // Auto-pause when tab is hidden mid-race
        document.addEventListener('visibilitychange', () => {
            const session = this.engine.getSession();
            if (!session || !this.isTestActive)
                return;
            if (document.hidden) {
                if (!session.isPaused && this.engine.getPhase() === 'racing') {
                    this.engine.pauseTest();
                    this.typingArea.togglePause(true);
                    this.caret.setVisible(false);
                    this.autoPausedByVisibility = true;
                }
            }
            else if (this.autoPausedByVisibility && session.isPaused) {
                // Leave paused — user resumes with Escape (avoids surprise resume)
                this.autoPausedByVisibility = false;
            }
        });
        const startButton = document.getElementById('start-test-btn');
        const resetButton = document.getElementById('reset-test-btn');
        if (startButton) {
            startButton.addEventListener('click', () => {
                this.startTest();
            });
        }
        if (resetButton) {
            resetButton.addEventListener('click', () => {
                this.resetTest();
            });
        }
        const typingArea = document.getElementById('typing-area-container');
        if (typingArea) {
            const focusMobileInput = () => {
                if (this.isMobile && this.mobileInput) {
                    this.mobileInput.focus();
                    setTimeout(() => {
                        if (this.mobileInput && document.activeElement !== this.mobileInput) {
                            this.mobileInput.focus();
                        }
                    }, 50);
                }
            };
            const scrollIntoViewMobile = () => {
                if (this.isMobile) {
                    setTimeout(() => {
                        const activeWord = document.querySelector('.word.active');
                        if (activeWord) {
                            activeWord.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                                inline: 'nearest',
                            });
                        }
                        else {
                            typingArea.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                                inline: 'nearest',
                            });
                        }
                    }, 300);
                }
            };
            typingArea.addEventListener('click', () => {
                if (this.isMobile && this.mobileInput) {
                    focusMobileInput();
                }
                else {
                    typingArea.focus();
                }
                scrollIntoViewMobile();
            });
            typingArea.addEventListener('touchend', (e) => {
                if (this.isMobile && this.mobileInput) {
                    e.preventDefault();
                    focusMobileInput();
                    scrollIntoViewMobile();
                }
            }, { passive: false });
            typingArea.addEventListener('focus', () => {
                if (this.isMobile && this.mobileInput) {
                    focusMobileInput();
                }
                scrollIntoViewMobile();
            });
            typingArea.setAttribute('tabindex', '0');
            typingArea.setAttribute('contenteditable', 'false');
            typingArea.addEventListener('contextmenu', (e) => {
                e.preventDefault();
            });
        }
    }
    startTest() {
        if (this.isTestActive)
            return;
        if (this.engine.getPhase() !== 'ready')
            return;
        sessionStorage.removeItem('typeracer-results');
        if (this.isMobile) {
            document.body.classList.add('typeracer-test-active');
        }
        this.engine.startTest();
        const session = this.engine.getSession();
        if (session) {
            this.typingArea.setActiveWord(session.activeWordIndex, session.activeLetterIndex);
        }
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.caret.setVisible(this.config.showCaret);
                const session = this.engine.getSession();
                if (session) {
                    this.typingArea.setActiveWord(session.activeWordIndex, session.activeLetterIndex);
                }
            });
        });
        const startButton = document.getElementById('start-test-btn');
        const resetButton = document.getElementById('reset-test-btn');
        if (startButton)
            startButton.style.display = 'none';
        if (resetButton)
            resetButton.style.display = 'block';
        const typingArea = document.getElementById('typing-area-container');
        if (typingArea) {
            setTimeout(() => {
                if (this.isMobile && this.mobileInput) {
                    this.mobileInput.focus();
                    this.mobileInput.value = '';
                    this.lastInputValue = '';
                    setTimeout(() => {
                        if (this.mobileInput && document.activeElement !== this.mobileInput) {
                            this.mobileInput.focus();
                        }
                    }, 50);
                }
                else {
                    typingArea.focus();
                }
            }, 100);
        }
    }
    resetTest() {
        this.engine.resetTest();
        this.isTestActive = false;
        this.autoPausedByVisibility = false;
        this.stopStatsUpdates();
        this.caret.setVisible(false);
        document.body.classList.remove('typeracer-test-active');
        this.engine.clearDictionaryCache();
        this.initializeSession().catch((err) => {
            console.error('reset initializeSession failed:', err);
        });
        const startButton = document.getElementById('start-test-btn');
        const resetButton = document.getElementById('reset-test-btn');
        if (startButton)
            startButton.style.display = 'block';
        if (resetButton)
            resetButton.style.display = 'none';
        if (this.mobileInput) {
            this.mobileInput.value = '';
            this.lastInputValue = '';
        }
    }
    updateStats() {
        const stats = this.engine.calculateStats();
        const session = this.engine.getSession();
        if (session) {
            this.statsDisplay.updateStats(stats, this.config.mode, this.config);
        }
    }
    startStatsUpdates() {
        this.statsUpdateInterval = window.setInterval(() => {
            this.updateStats();
        }, 100);
    }
    stopStatsUpdates() {
        if (this.statsUpdateInterval) {
            clearInterval(this.statsUpdateInterval);
            this.statsUpdateInterval = null;
        }
        this.updateStats();
    }
    handleModeChange(mode) {
        this.config.mode = mode;
        this.typingArea.setMode(mode);
        if (mode === 'paragraphs') {
            this.config.dictionary = 'reaalipoiss-paragraphs';
            this.config.wordCount = this.config.wordCount || 1;
        }
        else if (mode === 'time') {
            this.config.dictionary = 'reaalipoiss-newspaper';
        }
        else if (mode === 'words') {
            this.config.dictionary = 'reaalipoiss-newspaper';
            this.config.wordCount = this.config.wordCount || 10;
        }
        this.modeSelector.setMode(mode);
        this.saveConfig();
        if (!this.isTestActive) {
            this.initializeSession().catch((err) => console.error('mode change init failed:', err));
        }
    }
    handleConfigChange(updates) {
        this.config = { ...this.config, ...updates };
        this.modeSelector.setConfig(this.config);
        this.saveConfig();
        if (updates.fontSize !== undefined) {
            this.typingArea.setFontSize(updates.fontSize);
        }
        if (updates.blindMode !== undefined) {
            this.typingArea.setBlindMode(updates.blindMode);
        }
        if (updates.showCaret !== undefined) {
            this.caret.setVisible(updates.showCaret && this.isTestActive);
        }
        if (updates.soundEnabled !== undefined) {
            this.soundManager.setEnabled(updates.soundEnabled);
        }
        if (!this.isTestActive) {
            this.initializeSession().catch((err) => console.error('config change init failed:', err));
        }
    }
    togglePause() {
        if (!this.isTestActive)
            return;
        const session = this.engine.getSession();
        if (!session)
            return;
        const phase = this.engine.getPhase();
        if (phase !== 'racing' && phase !== 'paused')
            return;
        if (session.isPaused || phase === 'paused') {
            this.engine.resumeTest();
            this.typingArea.togglePause(false);
            this.caret.setVisible(this.config.showCaret);
            this.autoPausedByVisibility = false;
            if (this.isMobile && this.mobileInput) {
                this.mobileInput.focus();
            }
        }
        else {
            this.engine.pauseTest();
            this.typingArea.togglePause(true);
            this.caret.setVisible(false);
        }
    }
    async refreshAndRestart() {
        this.engine.resetTest();
        this.isTestActive = false;
        this.autoPausedByVisibility = false;
        this.stopStatsUpdates();
        this.caret.setVisible(false);
        this.engine.clearDictionaryCache();
        await this.initializeSession();
        const startButton = document.getElementById('start-test-btn');
        const resetButton = document.getElementById('reset-test-btn');
        if (startButton)
            startButton.style.display = 'block';
        if (resetButton)
            resetButton.style.display = 'none';
    }
    showResults() {
        const stats = this.engine.calculateStats();
        const session = this.engine.getSession();
        const keystrokeHistory = this.engine.getKeystrokeHistory();
        const resultData = {
            stats,
            config: this.config,
            sessionId: session?.sessionId,
            keystrokeHistory: keystrokeHistory || [],
            timestamp: Date.now(),
        };
        sessionStorage.setItem('typeracer-results', JSON.stringify(resultData));
        if (!this.config.isPracticeMode) {
            this.saveToHistory(resultData);
        }
        window.location.href = '/typeracer-results/';
    }
    saveToHistory(resultData) {
        try {
            const historyKey = 'typeracer-history';
            const maxHistorySize = 50;
            const existingHistory = localStorage.getItem(historyKey);
            let history = existingHistory ? JSON.parse(existingHistory) : [];
            history.unshift({
                stats: resultData.stats,
                config: resultData.config,
                timestamp: resultData.timestamp,
            });
            if (history.length > maxHistorySize) {
                history = history.slice(0, maxHistorySize);
            }
            localStorage.setItem(historyKey, JSON.stringify(history));
        }
        catch (error) {
            console.error('Failed to save history:', error);
        }
    }
    loadConfig() {
        try {
            const saved = localStorage.getItem('typeracer-config');
            if (saved) {
                const parsed = JSON.parse(saved);
                const config = { ...this.getDefaultConfig(), ...parsed };
                const validModes = [
                    'time',
                    'words',
                    'paragraphs',
                    'quote',
                    'zen',
                ];
                if (!config.mode || !validModes.includes(config.mode)) {
                    config.mode = 'time';
                }
                return config;
            }
        }
        catch (error) {
            console.warn('Failed to load saved config:', error);
        }
        return this.getDefaultConfig();
    }
    saveConfig() {
        try {
            localStorage.setItem('typeracer-config', JSON.stringify(this.config));
        }
        catch (error) {
            console.warn('Failed to save config:', error);
        }
    }
    getDefaultConfig() {
        return {
            mode: 'time',
            duration: 30,
            wordCount: 10,
            dictionary: 'reaalipoiss-newspaper',
            difficulty: 'medium',
            blindMode: false,
            layout: 'qwerty',
            fontSize: 'medium',
            stopOnError: false,
            showCaret: true,
            punctuation: false,
            numbers: false,
            soundEnabled: false,
        };
    }
}
document.addEventListener('DOMContentLoaded', () => {
    const appElement = document.getElementById('typeracer-app');
    if (appElement) {
        try {
            new TyperacerApp();
            console.log('TyperacerApp initialized');
        }
        catch (error) {
            console.error('Error initializing TyperacerApp:', error);
        }
    }
    else {
        console.error('typeracer-app element not found');
    }
});
